import { useOrg } from 'src/contexts/OrgContext';
import { useAuth } from 'src/hooks/auth/useAuth';
import { useNylasConnections } from './useNylas';
import { useNylasInboxStream } from './useNylasInboxStream';

/**
 * App-shell-level owner of the Nylas inbox SSE subscription.
 *
 * This is a side-effect-only hook (renders nothing). It is mounted ONCE near
 * the top of AppLayoutWithSidebarContent so the EventSource opens at the shell
 * and persists across navigation WITHIN the protected shell instance
 * (/inbox -> /deal/x -> /inbox stays inside the one ProtectedAppLayout
 * instance). AppLayoutWithSidebar is rendered in two distinct Route element
 * trees (protected + demo); crossing protected<->demo unmounts/remounts this
 * provider, but the gate is false by construction on the demo route so the
 * demo instance never opens a connection.
 *
 * SINGLE OWNER (004_state.txt:217-218): this provider is the ONLY caller of
 * useNylasInboxStream. useAriInboxThreads no longer opens its own stream — it
 * reads the SWR caches this stream keeps warm. Two owners would mean two
 * parallel EventSources to /sse/inbox.
 *
 * Gate: the stream only opens when the user is authenticated, auth has
 * initialized, a workspace is selected, we're not on a demo route, and a Nylas
 * connection exists. `isDemo` is route-authoritative — passed from the shell
 * (location.pathname.startsWith('/demo')) rather than inferred here.
 */
export function useInboxStreamProvider({ isDemo }: { isDemo: boolean }): void {
  const { hasSelectedWorkspace } = useOrg();
  const { isAuthenticated, isInitialized } = useAuth();

  // Gate the connections fetch behind the same conditions as the stream so a
  // demo route / signed-out shell never fires the /integrations/connections
  // request (demo-leak fix, default #4).
  const enabledForConnections =
    isAuthenticated && isInitialized && hasSelectedWorkspace && !isDemo;

  const { connections } = useNylasConnections({ enabled: enabledForConnections });
  const connectionId = connections[0]?.connectionId ?? null;

  const enabled = enabledForConnections && !!connectionId;

  useNylasInboxStream(connectionId, { enabled });
}
